import React, { useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { BoxFolderDefaultLocaleSelect } from 'src/components/box-folders/BoxFolderDefaultLocaleSelect';
import {
  BoxFolderExclusionsEditor,
  EXCLUSIONS_MODAL_WIDTH,
} from 'src/components/box-folders/BoxFolderExclusionsEditor';
import { getProbeIndicator, type ProbeState } from 'src/components/box-folders/BoxFolderProbeIndicator';
import { BoxFolderProbeStatus, isProbeAlertState } from 'src/components/box-folders/BoxFolderProbeStatus';
import { BOX_DEFAULT_PATHNAME_EXCLUSIONS, type SyncTargetLocale } from 'src/constants';
import { useUser } from 'src/contexts/UserContext';
import { useDebounce } from 'src/hooks/useDebounce';
import { useEnrollBoxFolder } from 'src/hooks/useEnrollBoxFolder';
import { useExclusionListDraft } from 'src/hooks/useExclusionListDraft';
import { usePathnameListDraft } from 'src/hooks/usePathnameListDraft';
import { useGetBoxFolderQuery } from 'src/store/bender-api';
import { parseBoxFolderInput } from 'src/utils/syncTarget';
import { normalizeBenderError } from 'src/utils/benderErrors';
import { validateFolderIdEntry } from 'src/utils/exclusionValidation';
import { Logger } from 'src/utils/logger';

const PROBE_DEBOUNCE_MS = 400;

interface AddBoxFolderModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Adds a Box folder to the sync registry.
 *
 * Access is verified before enrolling: Bender's enumerator would otherwise accept the
 * folder and only discover it cannot read it on the first scheduled run, leaving a
 * folder that looks enrolled but silently yields nothing.
 *
 * Exclusions and the default locale are optional and use the same controls and add-time
 * validation as the edit modal, so they apply from the very first scan. The pathname list opens
 * pre-filled with common work-in-progress patterns the user can remove, folded into a collapsed group.
 *
 * Uses plain local state rather than react-hook-form — the folder is a single field, and
 * the Add button's enabled state is `probeState === 'ok'` with no exclusion error showing.
 */
export const AddBoxFolderModal: React.FC<AddBoxFolderModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const { user } = useUser();

  const [rawInput, setRawInput] = useState('');
  const folderIds = useExclusionListDraft(validateFolderIdEntry);
  const pathnames = usePathnameListDraft(BOX_DEFAULT_PATHNAME_EXCLUSIONS);
  const [defaultLocale, setDefaultLocale] = useState<SyncTargetLocale | null>(null);
  const { enroll, isEnrolling: isAdding } = useEnrollBoxFolder();

  const folderId = parseBoxFolderInput(rawInput);
  const [probeId] = useDebounce(folderId ?? '', PROBE_DEBOUNCE_MS) as [string, boolean];

  const { data, isFetching, error, refetch } = useGetBoxFolderQuery(
    { folderId: probeId },
    { skip: !probeId || !open },
  );

  /**
   * Derived, not latched. A `useRef` latch keyed on the `isFetching` transition would
   * never release when RTK Query serves a warm cache synchronously; comparing
   * `probeId !== folderId` behaves identically on cold and warm caches.
   */
  const resolveProbeState = (): ProbeState => {
    if (!rawInput.trim()) return 'empty';
    if (!folderId) return 'unparseable';
    // Still typing, or the debounce hasn't caught up.
    if (probeId !== folderId) return 'debouncing';
    if (isFetching) return 'checking';
    if (error) {
      const { status } = normalizeBenderError(error);
      // 404 carries both user-fixable causes and cannot be reported as "no such folder":
      // Box answers `GET /folders/:id` with 404 for a folder that exists but is not shared
      // with our system account, because it will not confirm the existence of something
      // you cannot see. So a 404 means "wrong id, OR not shared yet" — verified against
      // the real API, where both cases came back 404.
      if (status === 404) return 'inaccessible';
      // Everything else is a failure the user cannot act on, so it must not carry the
      // share-the-folder instruction. Bender answers 502 when its own Box credentials or
      // app authorization are rejected, which sharing a folder cannot fix; a 403 is not
      // expected from this endpoint at all and is handled here defensively. Access is
      // unconfirmed in every one of these cases, so adding stays blocked — enrolling
      // anyway yields a folder that silently produces nothing.
      return 'unavailable';
    }
    // Rejects a stale response for a folder id the user has since changed.
    if (data?.id === folderId) return 'ok';
    return 'debouncing';
  };

  const probeState = resolveProbeState();
  const hasExclusionError = folderIds.validation !== null || pathnames.validation !== null;
  const canAdd = probeState === 'ok' && !isAdding && !hasExclusionError;

  const resetAndClose = () => {
    setRawInput('');
    folderIds.reset();
    pathnames.reset();
    setDefaultLocale(null);
    onClose();
  };

  const close = () => {
    if (isAdding) return;
    resetAndClose();
  };

  const handleAdd = async () => {
    if (!canAdd || !folderId) return;

    const userAlias = user?.username;
    if (!userAlias) {
      // Bender's userAlias is @IsNotEmpty(), so an empty one is a guaranteed 400.
      Logger.error('Cannot add a Box folder without a resolved user alias');
      createToast({ type: AlertType.ERROR, message: t('box_folder_error_generic') });
      return;
    }

    // A typed-but-unadded entry is committed rather than silently dropped; an invalid one blocks.
    const excludedFolderIds = folderIds.resolvePending();
    const excludedPathnames = pathnames.resolvePending();
    if (!excludedFolderIds || !excludedPathnames) return;

    const shouldClose = await enroll({
      folderId,
      folderName: data?.name ?? folderId,
      userAlias,
      excludedFolderIds,
      excludedPathnames,
      defaultLocale,
    });
    if (shouldClose) resetAndClose();
  };

  const indicator = getProbeIndicator({ folder: data, probeState, t });

  const showStatusAlert = isProbeAlertState(probeState);
  // Column spacing adds a margin even to an empty child, so an idle region is omitted, not zero-height.
  const showStatusRegion = probeState !== 'empty';

  return (
    // Fixed width (the edit modal's, shared by the exclusion editors) and, once the user types,
    // a reserved-height status region: probe results never resize the modal under the pointer.
    <Modal
      bodySpacingInset="none"
      data-testid="add-box-folder-modal"
      open={open}
      title={t('box_folder_add')}
      width={EXCLUSIONS_MODAL_WIDTH}
      onClose={close}
    >
      <Column spacing="450" spacingInset="500">
        <Column spacing="300">
          <Input
            data-testid="add-box-folder-input"
            label={t('box_folder_label')}
            placeholder={t('box_folder_placeholder')}
            suffix={indicator}
            value={rawInput}
            onChange={setRawInput}
          />
          <Text color="secondary" type="b200">{t('box_folder_help')}</Text>

          {showStatusRegion && <BoxFolderProbeStatus folder={data} probeState={probeState} />}
        </Column>

        <BoxFolderDefaultLocaleSelect
          isDisabled={isAdding}
          testIdPrefix="add-box-folder"
          value={defaultLocale}
          onChange={setDefaultLocale}
        />

        <Divider data-testid="add-box-folder-exclusions-divider" />

        <Column data-testid="add-box-folder-exclusions" spacing="300">
          <Column spacing="100">
            <Text type="b300">{t('box_folder_add_exclusions_heading')}</Text>
            <Text color="secondary" type="b200">{t('box_folder_add_exclusions_help')}</Text>
          </Column>
          <BoxFolderExclusionsEditor
            defaultPathnames={BOX_DEFAULT_PATHNAME_EXCLUSIONS}
            folderIds={folderIds}
            isDisabled={isAdding}
            pathnames={pathnames}
            testIdPrefix="add-box-folder"
          />
        </Column>
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200">
          {/*
            Re-check belongs here rather than inside the Alert: as a link-style button in
            flowing text its baseline never lined up, and after sharing the folder in Box
            re-probing is a footer-level action like the other two.
          */}
          {showStatusAlert && (
            <Button
              data-testid="add-box-folder-recheck-button"
              disabled={isAdding}
              type="secondary"
              onClick={() => { void refetch(); }}
            >
              {t('box_folder_recheck')}
            </Button>
          )}
          <Button data-testid="add-box-folder-cancel-button" disabled={isAdding} type="tertiary" onClick={close}>
            {t('form_global_cancel')}
          </Button>
          <Button
            data-testid="add-box-folder-submit-button"
            disabled={!canAdd}
            type="primary"
            onClick={() => { void handleAdd(); }}
          >
            {isAdding ? <Loader size="small" /> : t('box_folder_add_submit')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
